/**
 * Agent Run Store
 *
 * Generic Zustand store for managing batch agent run state.
 * Keyed by agentType — supports multiple concurrent agents.
 *
 * Orchestrates:
 *  1. Creating a run log row in agent_run_logs (DB-first)
 *  2. Calling the agent's batch executor
 *  3. Updating the log row with final stats
 */

import { create } from 'zustand';
import {
  createRunLog,
  updateRunLog,
  getRunLogs,
  type AgentRunLog,
  type AgentRunTriggeredBy,
  type RunScope,
  type BatchStats,
} from '../../lib/agentRunService';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Batch runner function signature — each agent provides its own. */
export type AgentBatchRunner = (
  workspaceId: string,
  scope: RunScope,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
) => Promise<BatchStats>;

export interface AgentRunProgress {
  done: number;
  total: number;
}

export interface AgentRunSlice {
  status: 'idle' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: AgentRunProgress;
  activeLogId: string | null;
  lastRunLog: AgentRunLog | null;
  recentLogs: AgentRunLog[];
  logsLoading: boolean;
  error: string | null;
}

function emptySlice(): AgentRunSlice {
  return {
    status: 'idle',
    progress: { done: 0, total: 0 },
    activeLogId: null,
    lastRunLog: null,
    recentLogs: [],
    logsLoading: false,
    error: null,
  };
}

// ── Store state ───────────────────────────────────────────────────────────────

interface AgentRunStoreState {
  /** Run state keyed by agentType */
  byAgent: Record<string, AgentRunSlice>;

  /**
   * Start a batch run for an agent.
   * Creates a run log row, executes the batchRunner, finalises the log.
   */
  startRun: (params: {
    agentType: string;
    workspaceId: string;
    triggeredBy: AgentRunTriggeredBy;
    scope: RunScope;
    runByUserId?: string | null;
    batchRunner: AgentBatchRunner;
  }) => Promise<void>;

  /** Cancel a running batch (abort the in-progress fetch loop). */
  cancelRun: (agentType: string) => void;

  /** Fetch recent run logs for an agent from the DB. */
  fetchRecentLogs: (workspaceId: string, agentType: string) => Promise<void>;
}

// ── In-memory abort controller map ───────────────────────────────────────────
// Not stored in Zustand (AbortController is not serialisable).
const abortControllers: Record<string, AbortController> = {};

// ── Store ─────────────────────────────────────────────────────────────────────

export const useAgentRunStore = create<AgentRunStoreState>((set, get) => ({
  byAgent: {},

  startRun: async ({ agentType, workspaceId, triggeredBy, scope, runByUserId, batchRunner }) => {
    // Prevent concurrent runs for the same agent
    const current = get().byAgent[agentType];
    if (current?.status === 'running') return;

    // Create abort controller for this run
    const controller = new AbortController();
    abortControllers[agentType] = controller;

    // Set status to running
    set((s) => ({
      byAgent: {
        ...s.byAgent,
        [agentType]: {
          ...(s.byAgent[agentType] ?? emptySlice()),
          status: 'running',
          progress: { done: 0, total: 0 },
          error: null,
          activeLogId: null,
        },
      },
    }));

    // 1. Create run log row
    const { data: logRow, error: logError } = await createRunLog({
      workspaceId,
      agentType,
      triggeredBy,
      scope,
      runByUserId: runByUserId ?? null,
    });

    if (logError || !logRow) {
      set((s) => ({
        byAgent: {
          ...s.byAgent,
          [agentType]: {
            ...(s.byAgent[agentType] ?? emptySlice()),
            status: 'failed',
            error: logError ?? 'Failed to create run log',
          },
        },
      }));
      return;
    }

    set((s) => ({
      byAgent: {
        ...s.byAgent,
        [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), activeLogId: logRow.id },
      },
    }));

    // 2. Execute the batch runner
    let finalStats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };
    let finalStatus: 'completed' | 'failed' | 'cancelled' = 'completed';
    let errorMessage: string | null = null;

    try {
      finalStats = await batchRunner(
        workspaceId,
        scope,
        (done, total) => {
          set((s) => ({
            byAgent: {
              ...s.byAgent,
              [agentType]: {
                ...(s.byAgent[agentType] ?? emptySlice()),
                progress: { done, total },
              },
            },
          }));
        },
        controller.signal,
      );

      if (controller.signal.aborted) {
        finalStatus = 'cancelled';
      }
    } catch (err) {
      finalStatus = 'failed';
      errorMessage = err instanceof Error ? err.message : 'Unknown error';
    }

    // 3. Finalise the run log
    await updateRunLog(logRow.id, finalStats, finalStatus, errorMessage);

    // 4. Refresh logs from DB
    const { data: logs } = await getRunLogs(workspaceId, agentType);

    set((s) => ({
      byAgent: {
        ...s.byAgent,
        [agentType]: {
          ...(s.byAgent[agentType] ?? emptySlice()),
          status: finalStatus,
          activeLogId: null,
          lastRunLog: logs[0] ?? null,
          recentLogs: logs,
          error: errorMessage,
        },
      },
    }));

    delete abortControllers[agentType];
  },

  cancelRun: (agentType) => {
    const controller = abortControllers[agentType];
    if (controller) {
      controller.abort();
    }
  },

  fetchRecentLogs: async (workspaceId, agentType) => {
    set((s) => ({
      byAgent: {
        ...s.byAgent,
        [agentType]: { ...(s.byAgent[agentType] ?? emptySlice()), logsLoading: true },
      },
    }));

    const { data: logs, error } = await getRunLogs(workspaceId, agentType);

    set((s) => ({
      byAgent: {
        ...s.byAgent,
        [agentType]: {
          ...(s.byAgent[agentType] ?? emptySlice()),
          logsLoading: false,
          recentLogs: logs,
          lastRunLog: logs[0] ?? null,
          error: error,
        },
      },
    }));
  },
}));
